import { useState, useRef, useEffect, useCallback } from "react";
import { Box, IconButton, Typography, Tooltip } from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import { colors } from "@components/ui/colors";

interface AudioRecorderLabels {
    discardTooltip?: string;
    stopTooltip?: string;
    confirmTooltip?: string;
    autoSendTooltip?: string;
    micAccessErrorMessage?: string;
}

interface Props {
    onRecordingComplete: (file: File) => void;
    onCancel: () => void;
    onAutoSend?: (file: File) => void;
    messageText?: string;
    labels?: AudioRecorderLabels;
}

function formatDuration(seconds: number) {
    const m = Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0");

    const s = (seconds % 60).toString().padStart(2, "0");

    return `${m}:${s}`;
}

export function AudioRecorder({
    onRecordingComplete,
    onCancel,
    onAutoSend,
    messageText = "",
    labels,
}: Props) {
    const [phase, setPhase] = useState<"idle" | "recording" | "preview">("idle");
    const [duration, setDuration] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [bars, setBars] = useState<number[]>(Array(32).fill(3));

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const fileRef = useRef<File | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const animFrameRef = useRef<number | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const stopAnimation = () => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }
    };

    const stopStream = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    };

    const resetState = () => {
        setPhase("idle");
        setDuration(0);
        setBars(Array(32).fill(3));
        fileRef.current = null;

        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }

        setAudioUrl(null);
    };

    const drawBars = useCallback(() => {
        const analyser = analyserRef.current;

        if (!analyser) return;

        const data = new Uint8Array(analyser.frequencyBinCount);

        analyser.getByteFrequencyData(data);

        const step = Math.max(1, Math.floor(data.length / 32));

        const newBars = Array.from({ length: 32 }, (_, index) => {
            const value = data[index * step] || 0;

            return Math.max(3, Math.round((value / 255) * 40));
        });

        setBars(newBars);
        animFrameRef.current = requestAnimationFrame(drawBars);
    }, []);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            streamRef.current = stream;

            const audioCtx = new AudioContext();

            audioCtxRef.current = audioCtx;

            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();

            analyser.fftSize = 256;
            source.connect(analyser);
            analyserRef.current = analyser;

            const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : "audio/ogg;codecs=opus";

            const mediaRecorder = new MediaRecorder(stream, { mimeType });

            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const extension = mimeType.includes("webm") ? "webm" : "ogg";

                const file = new File(
                    [blob],
                    `recording-${Date.now()}.${extension}`,
                    {
                        type: mimeType,
                    },
                );

                fileRef.current = file;

                if (audioUrl) {
                    URL.revokeObjectURL(audioUrl);
                }

                const url = URL.createObjectURL(blob);

                setAudioUrl(url);
                setPhase("preview");

                stopStream();

                void audioCtxRef.current?.close();
                audioCtxRef.current = null;
            };

            mediaRecorder.start();

            setDuration(0);
            setPhase("recording");

            timerRef.current = setInterval(() => {
                setDuration((currentDuration) => currentDuration + 1);
            }, 1000);

            animFrameRef.current = requestAnimationFrame(drawBars);
        } catch {
            alert(labels?.micAccessErrorMessage ?? "No se pudo acceder al micrófono.");
            onCancel();
        }
    };

    const stopRecording = () => {
        stopTimer();
        stopAnimation();

        if (mediaRecorderRef.current?.state === "recording") {
            mediaRecorderRef.current.stop();
        }
    };

    const discard = () => {
        stopTimer();
        stopAnimation();

        if (mediaRecorderRef.current?.state === "recording") {
            mediaRecorderRef.current.stop();
        } else {
            stopStream();
        }

        void audioCtxRef.current?.close();
        audioCtxRef.current = null;

        resetState();
        onCancel();
    };

    const confirmAndSend = () => {
        if (!fileRef.current) return;

        if (onAutoSend) {
            onAutoSend(fileRef.current);
        } else {
            onRecordingComplete(fileRef.current);
        }

        resetState();
    };

    useEffect(() => {
        void startRecording();

        return () => {
            stopTimer();
            stopAnimation();
            stopStream();

            void audioCtxRef.current?.close();
            audioCtxRef.current = null;

            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, []);

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            px={2}
            py={1.25}
            sx={{
                borderTop: "1px solid",
                borderColor: colors.orange[100],
            }}
        >
            <Tooltip title={labels?.discardTooltip ?? "Discard"}>
                <IconButton
                    size="small"
                    onClick={discard}
                    sx={{
                        color: colors.orange[700],
                        bgcolor: "transparent",
                        width: 34,
                        height: 34,
                        transition: "all 0.2s ease",
                        "&:hover": {
                            bgcolor: colors.orange[100],
                            color: colors.orange[900],
                        },
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Box flex={1} display="flex" alignItems="center" gap={1}>
                {phase === "recording" ? (
                    <>
                        <Box display="flex" alignItems="center" gap="2px" height={44} flex={1}>
                            {bars.map((height, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: "100%",
                                        maxWidth: 4,
                                        height: `${height}px`,
                                        bgcolor: colors.orange[500],
                                        borderRadius: 1,
                                        transition: "height 0.08s ease",
                                    }}
                                />
                            ))}
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{
                                fontFamily: "monospace",
                                color: colors.orange[800],
                                minWidth: 38,
                                fontWeight: 600,
                            }}
                        >
                            {formatDuration(duration)}
                        </Typography>
                    </>
                ) : (
                    <Box
                        component="audio"
                        controls
                        src={audioUrl ?? ""}
                        sx={{
                            width: "100%",
                            height: 36,
                            borderRadius: 2,
                            bgcolor: colors.orange[75],
                        }}
                    />
                )}
            </Box>

            {phase === "recording" ? (
                <Tooltip title={labels?.stopTooltip ?? "Detener"}>
                    <IconButton
                        size="small"
                        onClick={stopRecording}
                        sx={{
                            bgcolor: colors.orange[600],
                            color: "#fff",
                            width: 34,
                            height: 34,
                            boxShadow: `0 2px 8px ${colors.orange[200]}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                bgcolor: colors.orange[700],
                                boxShadow: `0 3px 10px ${colors.orange[300]}`,
                            },
                        }}
                    >
                        <StopIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip
                    title={
                        onAutoSend
                            ? (labels?.autoSendTooltip ?? "Enviar audio")
                            : (labels?.confirmTooltip ?? "Confirmar")
                    }
                >
                    <IconButton
                        size="small"
                        onClick={confirmAndSend}
                        sx={{
                            bgcolor: colors.orange[500],
                            color: "#fff",
                            width: 34,
                            height: 34,
                            boxShadow: `0 2px 8px ${colors.orange[200]}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                bgcolor: colors.orange[600],
                                boxShadow: `0 3px 10px ${colors.orange[300]}`,
                            },
                        }}
                    >
                        <CheckIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}